import React, { useState, useEffect } from "react";
import { useFetch } from "@/lib/api";
import { Download } from "lucide-react";
import { PageHeader, Panel, StatusChip, LoadingState, CopyButton, DemoBadge } from "@/components/app/primitives";
import { LiveReportFactsPanel } from "@/components/app/liveModulePanels";

export default function ReportBuilderPage() {
  const { data: reports, loading } = useFetch("/reports");
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (reports?.length && !selectedId) setSelectedId(reports[0].id);
  }, [reports, selectedId]);

  if (loading) return <LoadingState message="Staging report templates..." />;
  const report = (reports || []).find((r) => r.id === selectedId);

  const reportText = (r) => {
    if (!r) return "";
    let out = `${r.title}\nSIX x Brightspeed Transformation OS - Illustrative Demo Data\n${"=".repeat(60)}\n\n`;
    r.sections.forEach((s) => { out += `${s.heading.toUpperCase()}\n${"-".repeat(s.heading.length)}\n${s.body}\n\n`; });
    return out;
  };

  const downloadReport = (r) => {
    const blob = new Blob([reportText(r)], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${r.type}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="reports-page">
      <PageHeader
        kicker="Scene 11 - Executive report builder"
        title="Report Builder"
        subtitle="Copy- and export-ready executive outputs. Every report separates known facts, evidence-backed findings, hypotheses, open questions, and leadership decisions - by construction."
      />

      <LiveReportFactsPanel />

      <div className="grid lg:grid-cols-4 gap-4">
        <div className="lg:col-span-1 space-y-2" data-testid="report-selector">
          {(reports || []).map((r) => (
            <button key={r.id} onClick={() => setSelectedId(r.id)} data-testid={`report-tab-${r.type}`}
              className={`w-full text-left rounded-xl border p-3 transition-colors active:scale-[0.99] ${r.id === selectedId ? "border-[--six-crimson] bg-[--six-crimson-soft]" : "border-[--hairline] bg-[--panel-dark] hover:bg-[--shell-3]"}`}>
              <div className="font-mono text-[10px] text-[--tx-3] mb-0.5">{r.id}</div>
              <div className="text-[13px] font-semibold text-[--tx-1] leading-snug">{r.title}</div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-3">
          {report && (
            <Panel
              title={report.title}
              testId="report-preview"
              right={
                <div className="flex items-center gap-2">
                  <DemoBadge />
                  <CopyButton text={reportText(report)} label="Copy report" testId="report-copy-button" />
                  <button onClick={() => downloadReport(report)} data-testid="report-builder-export-button"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-[--six-crimson] hover:bg-[--six-crimson-hover] active:scale-[0.98] px-2.5 py-1.5 text-xs font-semibold text-white transition-colors">
                    <Download size={13} /> Export .txt
                  </button>
                </div>
              }
            >
              <div className="space-y-5">
                {report.sections.map((s, i) => (
                  <div key={i} data-testid={`report-section-${i}`}>
                    <h4 className="font-display text-[13px] font-semibold text-[#E86A6A] uppercase tracking-wide mb-1.5">{s.heading}</h4>
                    <p className="text-[13px] text-[--tx-2] leading-relaxed whitespace-pre-line">{s.body}</p>
                  </div>
                ))}
              </div>
              <div className="mt-5 pt-3 border-t border-[--divider] flex flex-wrap gap-2">
                <StatusChip tone="validated">Known facts labeled</StatusChip>
                <StatusChip tone="ai">Hypotheses separated</StatusChip>
                <StatusChip tone="risk">Decisions flagged</StatusChip>
                <StatusChip tone="neutral">No overclaiming</StatusChip>
              </div>
            </Panel>
          )}
        </div>
      </div>
    </div>
  );
}
